import { generateEmailHash } from '@hyphengroup/cms-components/utils/emailHash';
import CryptoJS from 'crypto-js';
import uuid from 'react-uuid';
import { track } from 'utils/tracking';
import { ExistingCreditCard } from '../../const';
import { ITrackingProductPageBaseParameters } from '../shared/baseParameters';
import { fillItems, ITrackingFillItemsParameters } from '../shared/fillItems';
import {
  fillMarketingConsent,
  ITrackingFillMarketingConsentParameters,
} from '../shared/fillMarketingConsent';
import {
  fillRewardCampaign,
  ITrackingFillRewardCampaignParameters,
} from '../shared/fillRewardCampaign';
import { CountryCode } from '$/shared/localization';

export interface ITrackApplyLeadFormSubmitParameters
  extends ITrackingFillMarketingConsentParameters,
    ITrackingFillRewardCampaignParameters,
    ITrackingFillItemsParameters,
    Pick<
      ITrackingProductPageBaseParameters,
      'leadId' | 'experiment_id' | 'variant'
    > {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  existingCreditCard: ExistingCreditCard;
  country: CountryCode;
  monthlyIncome: number;
  employmentStatus: string;
  CCTenure: string;
  bankIssued: string[];
  loanAmount?: number;
  loanTenure?: number; // in months
  businessOperatingDuration?: string;
  vertical?: string;
}

export const trackApplyLeadFormSubmit = (
  parameters: ITrackApplyLeadFormSubmitParameters,
): void => {
  const {
    email: _email,
    firstName: _firstName,
    lastName: _lastName,
    phoneNumber: _phoneNumber,
    existingCreditCard,
    leadId,
    country: _country,
    monthlyIncome,
    employmentStatus,
    CCTenure,
    bankIssued,
    experiment_id,
    variant,
    loanAmount,
    loanTenure,
    businessOperatingDuration,
    vertical,
  } = parameters;
  const email = _email.trim().toLocaleLowerCase();
  const emailHash = generateEmailHash(email);
  const firstName = _firstName.trim().toLocaleLowerCase();
  const lastName = _lastName.trim().toLocaleLowerCase();
  const phoneNumber = `63${_phoneNumber.replace(/\D/g, '')}`;
  const country = _country.toLocaleLowerCase();

  const userProfileTrack = buildUserProfileTrack({
    existingCreditCard,
    CCTenure: CCTenure,
    monthlyIncome,
    employmentStatus,
    bankIssued,
    loanAmount,
    loanTenure,
    businessOperatingDuration,
    vertical,
  });

  return track({
    event: 'form_submit',
    lead_form_name: 'apply-lead-detail-form',
    form_type: 'apply form',
    action: 'click',
    ...fillMarketingConsent(parameters),
    form_submission_id: uuid(),
    lead_id: leadId,
    ...fillRewardCampaign(parameters),
    search_result_parameter: '',
    ...fillItems(parameters),
    user_profile: userProfileTrack,
    user_data: {
      email,
      email_hash_md5: emailHash,
      email_hash_sha256: CryptoJS.SHA256(email).toString(),
      first_name: firstName,
      first_name_sha256: CryptoJS.SHA256(firstName).toString(),
      last_name: lastName,
      last_name_sha256: CryptoJS.SHA256(lastName).toString(),
      phone_number: phoneNumber,
      phone_number_sha256: CryptoJS.SHA256(phoneNumber).toString(),
      country,
      country_sha256: CryptoJS.SHA256(country).toString(),
    },
    userIdentities: {
      email,
      other: emailHash,
    },
    experiment_id,
    variant,
  });
};

interface UserProfileParams {
  existingCreditCard: ExistingCreditCard;
  CCTenure: string;
  monthlyIncome?: number;
  employmentStatus?: string;
  bankIssued?: string[];
  loanAmount?: number;
  loanTenure?: number; // in months
  businessOperatingDuration?: string;
  vertical?: string;
}

function buildUserProfileTrack({
  existingCreditCard,
  CCTenure,
  monthlyIncome = 0,
  employmentStatus = '',
  bankIssued = [],
  loanAmount,
  loanTenure,
  businessOperatingDuration,
  vertical,
}: UserProfileParams): string {
  const parts: string[] = [];

  // existing credit card
  if (existingCreditCard === ExistingCreditCard.No) {
    parts.push(`ecc-${existingCreditCard}`);
  } else {
    parts.push(`ecc-y,${CCTenure}`);
    if (bankIssued.length > 0) {
      parts.push(`prov-${bankIssued.sort().join('-')}`);
    }

    // BL excludes monthlyIncome and employmentStatus
    if (vertical !== 'BL') {
      parts.push(
        `mi-${String(monthlyIncome).replace(/\D+/g, '')}`,
        employmentStatus,
      );
    }
  }

  // PL & BL: include loan amount + tenure
  if (['PL', 'BL'].includes(vertical ?? '')) {
    if (vertical === 'BL' && businessOperatingDuration) {
      parts.push(`bod-${businessOperatingDuration}`);
    }
    parts.push(`amt-${loanAmount}`, `tne-${loanTenure} months`);
  }

  return parts.join('|');
}
